import React, { useState, useEffect, useRef } from "react";
import { styled } from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Collapse from "@mui/material/Collapse";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link, useLocation, useNavigate } from "react-router-dom";

import TcrcLogo from "./assets/images/logos/tcrc-logo.png";
import { keycloakLogout } from "./auth/logout";
import { useUserContext } from "./context/UserContext";
import { formatDateTime } from "./utils/dateFormat";

const pages = [
  { label: "Home", path: "/" },
  { label: "Client", path: "/client-management" },
  { label: "Project", path: "/project" },
  { label: "Subject", path: "/subject-management" },
  { label: "Questionnaire", path: "/questionnaire-management" },
  { label: "Reports", path: "/reports" },
];

const NAV_BASE_HEIGHT = 130;

const FixedNavShell = styled(Box)({
  position: "fixed",
  top: 0,
  left: 0,
  width: "100%",
  zIndex: 1300,
});

const TopHeader = styled(Box)({
  backgroundColor: "#ffffff",
  borderBottom: "1px solid #e0e0e0",
  display: "flex",
  flexDirection: "column",
});

const HeaderStack = styled(Box)({
  width: "100%",
});

const HeaderContent = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: 70,
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: theme.spacing(0, 2),
  gap: theme.spacing(1),
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(0, 3),
  },
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(0, 5),
  },
}));

const MobileMarqueeTrack = styled(Box)(({ theme }) => ({
  display: "none",
  overflow: "hidden",
  width: "100%",
  borderTop: "1px solid #e8eef2",
  backgroundColor: "#f7fafc",
  padding: theme.spacing(0.75, 0),
  [theme.breakpoints.down("md")]: {
    display: "block",
  },
}));

const MobileMarqueeInner = styled(Box)({
  display: "flex",
  width: "max-content",
  animation: "headerMarquee 28s linear infinite",
  "@keyframes headerMarquee": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-50%)" },
  },
});

const MobileMarqueeText = styled(Typography)({
  whiteSpace: "nowrap",
  fontSize: "0.8rem",
  color: "#1f2f3f",
  paddingRight: "3rem",
});

const LogoWrapper = styled(Box)(({ theme }) => ({
  height: 48,
  display: "flex",
  alignItems: "center",
  flexShrink: 0,
  [theme.breakpoints.up("md")]: {
    height: 64,
  },
}));

const Logo = styled("img")({
  height: "100%",
  width: "auto",
  maxWidth: "min(220px, 55vw)",
  display: "block",
  objectFit: "contain",
});

const HeaderRight = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  fontSize: "14px",
  color: "#1f2f3f",
  flexWrap: "wrap",
  justifyContent: "flex-end",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const SignOutButton = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  color: "#3f7fbf",
  fontWeight: 600,
  cursor: "pointer",
  transition: "0.2s ease",
  "&:hover": {
    color: "#2c5d8f",
  },
}));

const PersonaSwitchTrigger = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.75),
  color: "#3f7fbf",
  fontWeight: 600,
  fontSize: "14px",
  cursor: "pointer",
  transition: "0.2s ease",
  userSelect: "none",
  "&:hover": {
    color: "#2c5d8f",
  },
}));

const StyledAppBar = styled(AppBar)({
  backgroundColor: "#c7d7df",
  boxShadow: "none",
  border: "none",
  outline: "none",
  position: "static",
  width: "100%",
});

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 60,
  display: "flex",
  justifyContent: "center",
  position: "relative",
  maxWidth: "1280px",
  margin: "0 auto",
  width: "100%",
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

const DesktopMenuBox = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(4),
  flexWrap: "wrap",
  justifyContent: "center",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Separator = styled("span")({
  width: "1px",
  height: "22px",
  backgroundColor: "#3f7fbf",
  opacity: 0.4,
});

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  color: active ? "#3f7fbf" : "#1f2f3f",
  fontWeight: 600,
  fontSize: "16px",
  textTransform: "none",
  position: "relative",
  padding: "6px 4px",
  minWidth: "auto",
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: 0,
    width: active ? "100%" : "0%",
    height: "2px",
    backgroundColor: "#3f7fbf",
    transition: "width 0.3s ease",
  },
  "&:hover": {
    backgroundColor: "transparent",
    color: "#3f7fbf",
  },
  "&:hover::after": {
    width: "100%",
  },
}));

const MobileControls = styled(Box)(({ theme }) => ({
  position: "absolute",
  left: theme.spacing(1),
  right: theme.spacing(1),
  top: "50%",
  transform: "translateY(-50%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const MobileMenuPanel = styled(Box)(({ theme }) => ({
  backgroundColor: "#eef4f7",
  borderTop: "1px solid rgba(63, 127, 191, 0.25)",
  padding: theme.spacing(1.5, 2, 2),
}));

const MobileNavLink = styled(Button, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active, theme }) => ({
  justifyContent: "flex-start",
  width: "100%",
  textAlign: "left",
  color: active ? "#3f7fbf" : "#1f2f3f",
  fontWeight: active ? 700 : 600,
  fontSize: "1rem",
  textTransform: "none",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1.25, 1.5),
  backgroundColor: active ? "rgba(63, 127, 191, 0.12)" : "transparent",
  "&:hover": {
    backgroundColor: "rgba(63, 127, 191, 0.1)",
    color: "#3f7fbf",
  },
}));

const MobileMetaBox = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: "#ffffff",
  border: "1px solid #d7e3ea",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
  fontSize: "0.875rem",
  color: "#1f2f3f",
}));

const Navbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navShellRef = useRef(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [anchorElPersona, setAnchorElPersona] = useState(null);
  const [cstTime, setCstTime] = useState("");
  const [navHeight, setNavHeight] = useState(NAV_BASE_HEIGHT);
  const {
    username,
    fullName,
    loginTime,
    roles,
    activePersona,
    setActivePersona,
    canSwitchPersona,
  } = useUserContext();

  useEffect(() => {
    const updateCstTime = () => {
      setCstTime(formatDateTime(new Date()));
    };
    updateCstTime();
    const interval = setInterval(updateCstTime, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const node = navShellRef.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;

    const observer = new ResizeObserver(() => {
      setNavHeight(node.offsetHeight || NAV_BASE_HEIGHT);
    });
    observer.observe(node);
    setNavHeight(node.offsetHeight || NAV_BASE_HEIGHT);
    return () => observer.disconnect();
  }, [mobileMenuOpen]);

  const handleOpenPersonaMenu = (event) => {
    setAnchorElPersona(event.currentTarget);
  };

  const handleClosePersonaMenu = () => {
    setAnchorElPersona(null);
  };

  const formattedLoginTime = loginTime ? formatDateTime(loginTime) : "N/A";

  const handleLogoutClick = async () => {
    await keycloakLogout(window.location.origin);
  };

  const normalizedRoles = (roles || []).map((r) => String(r || "").toUpperCase());
  const isAdmin = normalizedRoles.includes("ADMIN");
  const isManager = normalizedRoles.includes("MANAGER");
  const isStaff = normalizedRoles.includes("STAFF");
  const hasStaff = isAdmin || isManager || isStaff;
  const usePatientNav = !hasStaff || (canSwitchPersona && activePersona === "patient");

  let visiblePages = pages;
  if (usePatientNav) {
    visiblePages = [{ label: "My questionnaires", path: "/" }];
  } else if (isStaff && !isAdmin && !isManager) {
    visiblePages = pages.filter(
      (p) =>
        p.path === "/subject-management" ||
        p.path === "/questionnaire-management" ||
        p.path === "/reports"
    );
  }

  const isActivePath = (path) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  const switchPersona = (persona) => {
    setActivePersona(persona);
    navigate("/", { replace: true });
    setMobileMenuOpen(false);
    handleClosePersonaMenu();
  };

  const displayName = fullName || username || "User";
  const mobileMarqueeText = `Welcome ${displayName} · ${cstTime} (CST) · Last login: ${formattedLoginTime}`;

  return (
    <>
      <FixedNavShell ref={navShellRef}>
        <TopHeader>
          <HeaderStack>
            <HeaderContent>
              <LogoWrapper>
                <Logo src={TcrcLogo} alt="TCRC Logo" />
              </LogoWrapper>

              <HeaderRight>
                <Typography>
                  Welcome <strong>{displayName}</strong>
                </Typography>
                <Typography>{cstTime} (CST)</Typography>
                <Typography>Last login: {formattedLoginTime}</Typography>

              {canSwitchPersona ? (
                <>
                  <Tooltip title="Switch between staff and subject portal">
                    <PersonaSwitchTrigger
                      id="labtrails-persona-trigger"
                      onClick={handleOpenPersonaMenu}
                      role="button"
                      tabIndex={0}
                      aria-label="Change portal"
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElPersona)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleOpenPersonaMenu(e);
                        }
                      }}
                    >
                      <SwapHorizIcon fontSize="small" />
                      Change portal
                    </PersonaSwitchTrigger>
                  </Tooltip>
                  <Menu
                    id="labtrails-persona-menu"
                    anchorEl={anchorElPersona}
                    open={Boolean(anchorElPersona)}
                    onClose={handleClosePersonaMenu}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    MenuListProps={{ "aria-labelledby": "labtrails-persona-trigger" }}
                  >
                    <MenuItem
                      selected={activePersona === "staff"}
                      onClick={() => switchPersona("staff")}
                    >
                      Staff portal
                    </MenuItem>
                    <MenuItem
                      selected={activePersona === "patient"}
                      onClick={() => switchPersona("patient")}
                    >
                      Subject portal
                    </MenuItem>
                  </Menu>
                </>
              ) : null}

              <SignOutButton onClick={handleLogoutClick}>
                <LogoutOutlinedIcon fontSize="small" />
                Sign Out
              </SignOutButton>
            </HeaderRight>
          </HeaderContent>

          <MobileMarqueeTrack aria-label="Session information">
            <MobileMarqueeInner>
              <MobileMarqueeText component="span">{mobileMarqueeText}</MobileMarqueeText>
              <MobileMarqueeText component="span" aria-hidden>
                {mobileMarqueeText}
              </MobileMarqueeText>
            </MobileMarqueeInner>
          </MobileMarqueeTrack>
          </HeaderStack>
        </TopHeader>

        <StyledAppBar position="static" elevation={0}>
          <StyledToolbar>
            <MobileControls>
              <IconButton
                aria-label={mobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={mobileMenuOpen}
                aria-controls="labtrails-mobile-nav-panel"
                onClick={() => setMobileMenuOpen((open) => !open)}
                sx={{ color: "#1f2f3f" }}
              >
                {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
              </IconButton>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: "#1f2f3f", px: 1, textAlign: "center", flex: 1 }}
                noWrap
              >
                {visiblePages.find((page) => isActivePath(page.path))?.label || "Menu"}
              </Typography>
              <Box sx={{ width: 40 }} />
            </MobileControls>

            <DesktopMenuBox>
              {visiblePages.map((page, index) => (
                <React.Fragment key={page.path}>
                  <StyledButton
                    component={Link}
                    to={page.path}
                    active={isActivePath(page.path) ? 1 : 0}
                  >
                    {page.label}
                  </StyledButton>
                  {index < visiblePages.length - 1 ? <Separator /> : null}
                </React.Fragment>
              ))}
            </DesktopMenuBox>
          </StyledToolbar>

          <Collapse in={mobileMenuOpen} timeout="auto" unmountOnExit>
            <MobileMenuPanel id="labtrails-mobile-nav-panel">
              {visiblePages.map((page) => (
                <MobileNavLink
                  key={page.path}
                  component={Link}
                  to={page.path}
                  active={isActivePath(page.path) ? 1 : 0}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {page.label}
                </MobileNavLink>
              ))}

              {canSwitchPersona ? (
                <>
                  <Divider sx={{ my: 1.5 }} />
                  <Typography variant="caption" sx={{ fontWeight: 700, px: 0.5, color: "text.secondary" }}>
                    View as
                  </Typography>
                  <MobileNavLink
                    active={activePersona === "staff" ? 1 : 0}
                    onClick={() => switchPersona("staff")}
                  >
                    Staff portal
                  </MobileNavLink>
                  <MobileNavLink
                    active={activePersona === "patient" ? 1 : 0}
                    onClick={() => switchPersona("patient")}
                  >
                    Subject portal
                  </MobileNavLink>
                </>
              ) : null}

              <MobileMetaBox>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<LogoutOutlinedIcon />}
                  onClick={handleLogoutClick}
                  sx={{ alignSelf: "flex-start" }}
                >
                  Sign out
                </Button>
              </MobileMetaBox>
            </MobileMenuPanel>
          </Collapse>
        </StyledAppBar>
      </FixedNavShell>

      <Box sx={{ height: navHeight, flexShrink: 0 }} aria-hidden />
    </>
  );
};

export default Navbar;
